<script setup lang="ts">
import { computed } from 'vue'

/**
 * Button styled with Tailwind utilities, replicating the exact Bootstrap 3
 * metrics the app was originally built with (root font-size is 10px, so all
 * values are explicit px). Renders a <button> by default; pass as="a" for
 * link buttons (href/target fall through).
 */
const props = withDefaults(
  defineProps<{
    variant?: 'default' | 'primary' | 'success' | 'link'
    size?: 'md' | 'lg' | 'xs'
    as?: 'button' | 'a'
  }>(),
  { variant: 'default', size: 'md', as: 'button' },
)

const BASE =
  'inline-block font-normal text-center whitespace-nowrap align-middle ' +
  'touch-manipulation cursor-pointer select-none border no-underline ' +
  'hover:no-underline focus:no-underline active:outline-0 ' +
  'disabled:cursor-not-allowed disabled:opacity-65 disabled:shadow-none'

const VARIANTS = {
  default:
    'text-[#333] bg-white border-[#ccc] ' +
    'hover:text-[#333] hover:bg-[#e6e6e6] hover:border-[#adadad] ' +
    'focus:text-[#333] focus:bg-[#e6e6e6] focus:border-[#adadad] ' +
    'active:bg-[#e6e6e6] active:border-[#adadad] ' +
    'active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]',
  primary:
    'text-white bg-[#337ab7] border-[#2e6da4] ' +
    'hover:text-white hover:bg-[#286090] hover:border-[#204d74] ' +
    'focus:text-white focus:bg-[#286090] focus:border-[#204d74] ' +
    'active:bg-[#286090] active:border-[#204d74] ' +
    'active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]',
  success:
    'text-white bg-[#5cb85c] border-[#4cae4c] ' +
    'hover:text-white hover:bg-[#449d44] hover:border-[#398439] ' +
    'focus:text-white focus:bg-[#449d44] focus:border-[#398439] ' +
    'active:bg-[#449d44] active:border-[#398439] ' +
    'active:shadow-[inset_0_3px_5px_rgba(0,0,0,0.125)]',
  link:
    'text-[#337ab7] bg-transparent border-transparent shadow-none ' +
    'hover:text-[#23527c] hover:underline hover:bg-transparent ' +
    'focus:text-[#23527c] focus:underline focus:bg-transparent ' +
    'active:shadow-none',
}

const SIZES = {
  md: 'px-[12px] py-[6px] text-[14px] leading-[1.42857143]',
  lg: 'px-[16px] py-[10px] text-[18px] leading-[1.3333333]',
  xs: 'px-[5px] py-[1px] text-[12px] leading-[1.5]',
}

// Bootstrap 3 cascade: .btn-link zeroes the radius, but size classes are
// defined later in the sheet, so btn-link + btn-lg still got 6px.
const radius = computed(() => {
  if (props.variant === 'link' && props.size === 'md') return 'rounded-none'
  return { md: 'rounded-[4px]', lg: 'rounded-[6px]', xs: 'rounded-[3px]' }[props.size]
})

const classes = computed(() => [BASE, VARIANTS[props.variant], SIZES[props.size], radius.value])
</script>

<template>
  <component :is="props.as" :class="classes"><slot /></component>
</template>
